// Modules
import {createApi} from '@reduxjs/toolkit/query/react';

// Utils
import {axiosBaseQuery} from 'config/axiosBaseQuery';
import {
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  UpdatePasswordDto,
  CreateMessageDto,
  CustomerDocument,
  SignedUrlResponse,
  UploadDocumentRequest,
  CustomerMessage,
} from './customersTypes';
import {Project as ProjectListResponse} from 'features/projects/projectsTypes';

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Customers',
    'Customer',
    'CustomerProjects',
    'CustomerDocuments',
    'Projects',
    'Project',
  ],
  endpoints: builder => ({
    createCustomer: builder.mutation<Customer, CreateCustomerDto>({
      query: data => ({url: '/api/customers', method: 'POST', data}),
      invalidatesTags: ['Customers'],
    }),

    createCustomerForProject: builder.mutation<
      Customer,
      CreateCustomerDto & {projectId: string}
    >({
      query: ({projectId, ...data}) => ({
        url: '/api/customers',
        method: 'POST',
        data,
      }),
      invalidatesTags: (_res, _err, {projectId}) => {
        return [{type: 'Project', id: projectId}, 'Projects', 'Customers'];
      },
    }),

    fetchCustomers: builder.query<Customer[], void>({
      query: () => ({url: '/api/customers', method: 'GET'}),
      providesTags: ['Customers'],
    }),

    getCustomer: builder.query<Customer, string>({
      query: id => ({url: `/api/customers/${id}`, method: 'GET'}),
      providesTags: (_r, _e, id) => [{type: 'Customer', id}],
    }),

    updateCustomer: builder.mutation<
      Customer,
      {id: string; data: UpdateCustomerDto}
    >({
      query: ({id, data}) => ({
        url: `/api/customers/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_r, _e, {id}) => [{type: 'Customer', id}, 'Customers'],
    }),

    deleteCustomer: builder.mutation<void, string>({
      query: id => ({url: `/api/customers/${id}`, method: 'DELETE'}),
      invalidatesTags: ['Customers'],
    }),

    updateCustomerPassword: builder.mutation<
      void,
      {id: string; data: UpdatePasswordDto}
    >({
      query: ({id, data}) => ({
        url: `/api/customers/${id}/password`,
        method: 'PUT',
        data,
      }),
    }),

    addCustomerProject: builder.mutation<
      Customer,
      {id: string; projectId: string}
    >({
      query: ({id, projectId}) => ({
        url: `/api/customers/${id}/projects`,
        method: 'POST',
        data: {projectId},
      }),
      invalidatesTags: (_r, _e, {id}) => [{type: 'CustomerProjects', id}],
    }),

    removeCustomerProject: builder.mutation<
      Customer,
      {id: string; projectId: string}
    >({
      query: ({id, projectId}) => ({
        url: `/api/customers/${id}/projects/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, {id}) => [{type: 'CustomerProjects', id}],
    }),

    getCustomerProjects: builder.query<ProjectListResponse[], string>({
      query: id => ({url: `/api/customers/${id}/projects`, method: 'GET'}),
      providesTags: (_r, _e, id) => [{type: 'CustomerProjects', id}],
    }),

    // Customer Portal - uses customer JWT token
    getMyProjects: builder.query<ProjectListResponse[], void>({
      query: () => ({url: '/api/customers/me/projects', method: 'GET'}),
      providesTags: ['CustomerProjects'],
    }),

    uploadCustomerDocument: builder.mutation<
      CustomerDocument,
      UploadDocumentRequest
    >({
      query: ({formData}) => ({
        url: '/api/customers/documents/upload',
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      }),
      invalidatesTags: ['CustomerDocuments'],
    }),

    getCustomerDocumentUrl: builder.query<SignedUrlResponse, string>({
      query: documentId => ({
        url: `/api/customers/documents/${documentId}`,
        method: 'GET',
      }),
    }),

    deleteCustomerDocument: builder.mutation<void, string>({
      query: documentId => ({
        url: `/api/customers/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomerDocuments'],
    }),

    getAdminMessages: builder.query<CustomerMessage[], string>({
      query: customerId => ({
        url: `/api/customers/${customerId}/messages`,
        method: 'GET',
      }),
    }),

    sendAdminMessage: builder.mutation<
      CustomerMessage,
      {customerId: string; message: string}
    >({
      query: ({customerId, message}) => ({
        url: `/api/customers/${customerId}/messages`,
        method: 'POST',
        data: {message},
      }),
    }),

    getCustomerMessages: builder.query<CustomerMessage[], void>({
      query: () => ({
        url: `/api/customers/me/messages`,
        method: 'GET',
      }),
    }),

    sendCustomerMessage: builder.mutation<CustomerMessage, CreateMessageDto>({
      query: ({message}) => ({
        url: `/api/customers/me/messages`,
        method: 'POST',
        data: {message},
      }),
    }),
  }),
});

export const {
  useCreateCustomerMutation,
  useCreateCustomerForProjectMutation,
  useFetchCustomersQuery,
  useGetCustomerQuery,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useUpdateCustomerPasswordMutation,
  useAddCustomerProjectMutation,
  useRemoveCustomerProjectMutation,
  useGetCustomerProjectsQuery,
  useGetMyProjectsQuery,
  useUploadCustomerDocumentMutation,
  useGetCustomerDocumentUrlQuery,
  useDeleteCustomerDocumentMutation,
  useGetAdminMessagesQuery,
  useSendAdminMessageMutation,
  useGetCustomerMessagesQuery,
  useSendCustomerMessageMutation,
} = customersApi;
